import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { NotFoundPage } from '../NotFoundPage/NotFoundPage';
import styles from './RestaurantPage.module.scss';

import { cartStore } from '@/entities/cart/store/cartStore';
import { CompanyApi } from '@/entities/company/api';
import { companyStore } from '@/entities/company/store/companyStore';
import { RestaurantApi } from '@/entities/restaurant/api';
import { restaurantStore } from '@/entities/restaurant/store/restaurantStore';
import { OrderNumber } from '@/features/orders/ui/OrderNumber/OrderNumber';
import { StatusModal } from '@/features/orders/ui/OrderPaymentStatus/StatusModal';
import { ProductsList } from '@/features/products/ui/ProductsList/ProductsList';
import { RestaurantHeader } from '@/features/restaurants/ui/RestaurantHeader/RestaurantHeader';
import { RestaurantSlider } from '@/features/restaurants/ui/RestaurantSlider/RestaurantSlider';
import { StatusEnum } from '@/shared/api';
import { ROUTES } from '@/shared/constants/routes';
import { usePaymentFlow } from '@/shared/hooks/usePaymentFlow';
import { formatPhone } from '@/shared/lib/formatPhone';
import { observer } from '@/shared/lib/signal/observer';
import { InnerHtml } from '@/shared/ui-kit/InnerHtml/InnerHtml';
import { ProgressLoader } from '@/shared/ui-kit/ProgressLoader/ProgressLoader';

export const RestaurantPage: React.FC = observer(() => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const restaurant = restaurantStore.current.value;
  const company = companyStore.company.value;

  const {
    closeFailModal,
    visibleFailModal,
    orderStatusModalVisible,
    closeOrderStatusModal,
    orderStatusModalSelectedOrder,
  } = usePaymentFlow();

  const handleCloseAll = useCallback(() => {
    closeOrderStatusModal();
    if (code) {
      navigate(ROUTES.restaurant(code), { replace: true });
    }
  }, [closeOrderStatusModal, code, navigate]);

  const infoTopRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let mounted = true;
    if (!code) return;

    const requestedId = Number(code);

    setLoading(true);

    RestaurantApi.getRestaurant(requestedId)
      .then((r) => {
        if (!mounted) return r;

        const prevId = restaurantStore.current.value?.id;

        restaurantStore.setRestaurant(r);

        if (!prevId || prevId !== r.id) {
          cartStore.clear();
        }

        cartStore.loadCart();
        return r;
      })
      .then((r) => {
        if (!r) return;
        return CompanyApi.getCompany(r.company).then((c) => {
          if (mounted) companyStore.setCompany(c);
        });
      })
      .catch(() => {
        if (!mounted) return;
        restaurantStore.setRestaurant(null);
        cartStore.clear();
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      restaurantStore.setRestaurant(null);
    };
  }, [code]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <ProgressLoader mode="spinner" active={loading} done={false} />
      </div>
    );
  }

  if (!restaurant) {
    return <NotFoundPage />;
  }

  const title = `${restaurant.name} — Order2Go.ru`;
  return (
    <div className={styles.page}>
      <title>{title}</title>

      <RestaurantSlider infoRef={infoTopRef} />

      <div className={styles.infoBlock} ref={infoTopRef}>
        <div className={styles.infoInner}>
          <RestaurantHeader restaurant={restaurant} />
          <div className={styles.divider} />
          <ProductsList restaurantId={restaurant.id} />
        </div>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerSection}>
          <div className={styles.footerName}>
            {restaurant.name}, {new Date().getFullYear()}
          </div>
          {restaurant.address && <div>{restaurant.address}</div>}
          {restaurant.description && <div>{restaurant.description}</div>}
          {restaurant.phone && <div>{formatPhone(restaurant.phone)}</div>}
        </div>
        {!!company && (
          <>
            {!!company.documents?.length && (
              <div className={styles.divider} style={{ margin: 0 }} />
            )}
            {company.documents?.map((doc) => (
              <a
                className={styles.footerLink}
                href={doc.file}
                target="_blank"
                key={doc.name}
                rel="noreferrer"
              >
                {doc.name}
              </a>
            ))}
          </>
        )}

        {restaurant.companyDescription && (
          <>
            <div className={styles.divider} style={{ margin: 0 }} />
            <div className={styles.footerSection}>
              <InnerHtml
                html={restaurant.companyDescription.replace(
                  /(?:\r\n|\r|\n)/g,
                  '<br>'
                )}
              />
            </div>
          </>
        )}
      </footer>

      <StatusModal
        image="success"
        open={
          orderStatusModalVisible &&
          orderStatusModalSelectedOrder?.status === StatusEnum.PAID
        }
        onClose={closeOrderStatusModal}
        headerText="Всё получилось"
        title="Успешная оплата"
        subtitle="Передаём ваш заказ на кассу, это может занять немного времени"
        btnText="Отлично"
        showLoader
        onAction={() => {
          closeOrderStatusModal();
        }}
      />

      <StatusModal
        image="error"
        open={visibleFailModal}
        onClose={closeFailModal}
        headerText="Не удалось принять заказ"
        title="Что-то пошло не так"
        subtitle="Нам не удалось принять заказ, деньги вернутся вам на счёт"
        btnText="Понятно"
        onAction={closeFailModal}
      />

      <OrderNumber
        open={
          orderStatusModalVisible &&
          orderStatusModalSelectedOrder?.status === StatusEnum.ACCEPTED
        }
        onClose={handleCloseAll}
      />
    </div>
  );
});
